import api from "@/services/api";

const { post, put, get, delete: del, deleteOnUnload } = api.getPrefixedMethods("orderinfo");

// Top-level OrderInfo DateField columns. HTML <input type="date"> v-models become ""
// when the user clears the field, but the save-order endpoint rejects "" with 400
// (LAM-3863) because "" is indistinguishable from "no value" at the wire layer.
// Normalize to null here so cleared dates round-trip as a no-op (or explicit clear)
// instead of failing the save.
const ORDER_INFO_DATE_FIELDS = ["patientDob", "treatmentStartDate"];

function normalizeOrderInfoDateFields(data) {
  if (!data || typeof data !== "object") return data;
  const normalized = { ...data };
  for (const field of ORDER_INFO_DATE_FIELDS) {
    if (normalized[field] === "") {
      normalized[field] = null;
    }
  }
  return normalized;
}

// Drop an UNSET fax-form selection from order-creating payloads (LAM-5197). The previous
// release's create endpoints pass the body into OrderInfo(**data), which rejects the key even
// when null — omitting it keeps every non-opted-in tab working across a rollback; a tab with an
// actual selection in flight is the accepted (refresh-to-fix) skew documented in the PR.
function omitUnsetFaxFormSelection(data) {
  if (!data || typeof data !== "object" || data.faxSubmissionFormDetail != null) return data;
  const rest = { ...data };
  delete rest.faxSubmissionFormDetail;
  return rest;
}

// Saving the order posted the whole thing, question definitions included — 6 to 7 MB of body on a
// CMM order (LAM-5881). It posts one `{questionId, answer}` pair per question the screen declares —
// every visible one, with an empty answer where the user left it blank — and the server resolves
// each pair against the stored definition, which is the only place that knows how a question type
// answers. The declared ids are the scope of the write: a question behind an unsatisfied conditional
// is on no screen, is declared by nobody, and keeps the answer it holds. The screen that showed the
// questions passes its own set here, so this save is the only request that carries them.
function toAnswerPairs(data, declaredQuestionIds) {
  if (!data || typeof data !== "object") return data;

  const body = { ...data };
  delete body.payerQuestions;
  delete body.checklist;
  delete body.portalPayerQuestionsJson;
  if (!Array.isArray(declaredQuestionIds)) return body;

  const chosenOptions = new Map(
    (Array.isArray(data.checklist) ? data.checklist : []).map((item) => [item?.id, item?.options])
  );
  body.payerQuestionAnswers = [...new Set(declaredQuestionIds)].map((questionId) => {
    const options = chosenOptions.get(questionId);
    return {
      questionId,
      answer: (Array.isArray(options) ? options : []).map((option) => ({ id: option?.id, label: option?.label })),
    };
  });
  return body;
}

export const orderInfoService = {
  submitOrderInfo(data) {
    return post("", data);
  },
  validateAccess(data) {
    return post("access-initiated-prior-auth", data);
  },
  getListOfOrders(
    index,
    status,
    patient,
    date,
    dateFrom,
    dateTo,
    orderBy,
    tags,
    pageSize,
    modifiedBy,
    assignedUser,
    tagsMatch,
    dueFrom,
    dueTo,
    extraParams = {}
  ) {
    const params = { page: index, status };
    if (patient) params.patient = patient;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (!dateFrom && !dateTo && date) params.date = date;
    if (orderBy) params.order_by = orderBy;
    if (tags) params.tags = tags;
    if (tagsMatch) params.tags_match = tagsMatch;
    if (dueFrom) params.due_from = dueFrom;
    if (dueTo) params.due_to = dueTo;
    if (pageSize) params.page_size = pageSize;
    if (modifiedBy) params.modified_by = modifiedBy;
    if (assignedUser) params.assigned_user = assignedUser;
    return get("", { params: { ...params, ...extraParams } });
  },
  getOrderStatusCounts(filters = {}) {
    const params = {};
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    return get("submission-counts", { params });
  },
  uploadDecisionDocument(orderKey, data) {
    return put(`upload-decision-document/${orderKey}`, data);
  },
  draftAppeal(orderKey, data) {
    return post(`submission/${orderKey}/draft-appeal/`, data);
  },
  getAppealDraft(orderKey, taskId) {
    return get(`submission/${orderKey}/draft-appeal/`, { params: { task_id: taskId } });
  },
  saveAppealDraft(orderKey, data) {
    return put(`submission/${orderKey}/draft-appeal/`, data);
  },
  fillAppealCoverSheet(orderKey, data) {
    return post(`submission/${orderKey}/appeal-cover-sheet/`, data);
  },
  getAppealCoverSheet(orderKey, taskId) {
    return get(`submission/${orderKey}/appeal-cover-sheet/`, { params: { task_id: taskId } });
  },
  // Autofill the create-submission form from the order's uploaded documents (LAM-4379).
  // The response's field names arrive camelized, so they line up with orderInfo's own keys.
  startCreateSubmissionAutofill(orderKey, data) {
    return post(`submission/${orderKey}/autofill/`, data);
  },
  getCreateSubmissionAutofill(orderKey, taskId) {
    return get(`submission/${orderKey}/autofill/`, { params: { task_id: taskId } });
  },
  // Saves the posted draft, then re-reads the documents and re-answers the payer questions (LAM-5655).
  // The order shows Processing until the answers are merged and it returns to clinical review.
  // The draft travels as the declared answer pairs, like every other save of the review page (LAM-5881).
  regenerateAnswers(orderKey, data, declaredQuestionIds) {
    return post(
      `submission/${orderKey}/regenerate-answers/`,
      toAnswerPairs(normalizeOrderInfoDateFields(data), declaredQuestionIds)
    );
  },
  stopRegeneratingAnswers(orderKey) {
    return del(`submission/${orderKey}/regenerate-answers/`);
  },
  // {running, checking, startedOn, status}: what the review page polls during a run (no order payload).
  getRegenerationState(orderKey) {
    return get(`submission/${orderKey}/regenerate-answers/`);
  },
  changeOrderFormulary(orderKey, data) {
    return put(`change-order-formulary/${orderKey}`, data);
  },
  changeFormSelection(orderKey, data) {
    return put(`change-form-selection/${orderKey}`, data);
  },
  reopenSubmission(orderKey, data, declaredQuestionIds) {
    return put(`reopen-submission/${orderKey}`, toAnswerPairs(normalizeOrderInfoDateFields(data), declaredQuestionIds));
  },
  searchOrders(searchTerm, index, pageSize) {
    const params = { page: index };
    if (pageSize) params.page_size = pageSize;
    return post("search", searchTerm, { params });
  },
  getOrderDetails(id) {
    return get(`submission/${id}`);
  },
  getFreshDocumentLink(orderKey, url) {
    return post(`submission/${orderKey}/document-link`, { url });
  },
  getOrderDocuments(orderKey) {
    return get(`submission/${orderKey}/documents`);
  },
  getOrderAuditLogs(orderKey, pagingOptions) {
    return get(`submission/${orderKey}/audits`, { params: pagingOptions });
  },
  startOrderStaffTime(documentKey) {
    return post(`submission/${documentKey}/staff-time`);
  },
  pushOrderStaffTime(documentKey) {
    return del(`submission/${documentKey}/staff-time`);
  },
  pullAutofillingData(orderKey, data) {
    return post(`submit-autofill-data/${orderKey}`, data);
  },
  getAvailableAddressBooks() {
    return get("available-address-books");
  },
  createEmptyBenefitVerificationRequest(data) {
    return post("create-benefit-verification", data);
  },
  saveBenefitVerificationRequest(orderKey, data) {
    return put(`save-benefit-verification/${orderKey}`, data);
  },
  createEmptyOrder(data) {
    return post("create-empty-order", omitUnsetFaxFormSelection(normalizeOrderInfoDateFields(data)));
  },
  // Creates one submission per queued additional medication, all sharing sharedFields
  // (patient/provider/insurance/diagnosis/docs) — used only when Create Submission has more
  // than one medication. The primary medication keeps using createEmptyOrder/saveOrder.
  createSubmissionsBatch(sharedFields, medications) {
    return post("create-submissions-batch", {
      sharedFields: omitUnsetFaxFormSelection(normalizeOrderInfoDateFields(sharedFields)),
      medications: medications.map((medication) => normalizeOrderInfoDateFields(medication)),
    });
  },
  createNewOrder(data) {
    return post("create-order", data);
  },
  saveOrder(orderKey, data, declaredQuestionIds) {
    return put(`save-order/${orderKey}`, toAnswerPairs(normalizeOrderInfoDateFields(data), declaredQuestionIds));
  },
  applyPatientField(orderKey, field) {
    return post(`submission/${orderKey}/apply-patient-field/`, { field });
  },
  checkProviderDelegateAgreement(orderKey, providerFields) {
    return post(`submission/${orderKey}/provider-delegate-agreement-check/`, providerFields);
  },
  submitPdfForm(data) {
    return post("pdf-submission", data);
  },
  getPdfSubmissionDetails(id) {
    return get(`pdf-submission/${id}`);
  },
  updatePdfSubmission(id, data) {
    return put(`pdf-submission/${id}`, data);
  },
  fetchPatientRecords(orderKey) {
    return get(`patient-records/${orderKey}`);
  },
  deleteOrder(orderKey) {
    return del(`submission/${orderKey}`);
  },
  getSupportingDocumentsPdf(orderKey) {
    return get(`supporting-documents-pdf/${orderKey}`, {
      responseType: "blob",
      headers: { "Content-Type": "application/pdf" },
      _returnFullResponse: true,
      _skipPropsAdaptation: true,
    });
  },
  getFilledFormPdf(orderKey) {
    return get(`filled-form-pdf/${orderKey}`, {
      responseType: "blob",
      headers: { "Content-Type": "application/pdf" },
      _returnFullResponse: true,
      _skipPropsAdaptation: true,
    });
  },
  archiveOrder(orderKey) {
    return put(`submission/${orderKey}/archive/`);
  },
  unarchiveOrder(orderKey) {
    return del(`submission/${orderKey}/archive/`);
  },
  changeSubmissionMethod(orderKey, data) {
    return put(`submission/${orderKey}/change-submission-method/`, data);
  },
  changeRecordedStatus(orderKey, data) {
    return put(`submission/${orderKey}/change-recorded-status/`, data);
  },
  sendToPrescriber(orderKey, data) {
    return post(`send-to-prescriber/${orderKey}/`, data);
  },
  getPrescriberTemplate(orderKey) {
    return get(`prescriber-template/${orderKey}/`);
  },
  importCMMSubmission(data) {
    return post("cmm-submissions/", data);
  },
  updateSubmissionTags(orderKey, tags, tagDueDates) {
    return put(`submission/${orderKey}/tags/`, { tags, tagDueDates });
  },
  reportSubmissionIssue(orderKey, data) {
    return post(`submission/${orderKey}/report-issue/`, data);
  },
  requestDrugIndicationOverride(orderKey, data) {
    return post(`submission/${orderKey}/drug-indication-override/`, data);
  },
  withdrawDrugIndicationOverride(orderKey, medication) {
    return del(`submission/${orderKey}/drug-indication-override/`, { params: { medication } });
  },
  getAutofillStatus(orderKey) {
    return get(`submission/${orderKey}/autofill-status/`);
  },
  acquireOrderLock(orderKey) {
    return post(`submission/${orderKey}/locks/`);
  },
  getOrderLock(orderKey) {
    return get(`submission/${orderKey}/locks/`);
  },
  releaseOrderLock(orderKey) {
    return del(`submission/${orderKey}/locks/`);
  },
  releaseOrderLockOnUnload(orderKey) {
    return deleteOnUnload(`submission/${orderKey}/locks/`);
  },
  getSubmissionLocks(orderKeys) {
    return get("", { params: { only: "locks", keys: orderKeys.join(",") } });
  },
};
